import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../context/PageChromeContext';
import { useAtsMatchRun } from '../context/AtsMatchRunContext';
import {
  FiMapPin,
  FiExternalLink,
  FiRefreshCw,
  FiEdit,
  FiTrash2,
  FiZap,
  FiUser,
  FiAward,
  FiArrowLeft,
} from 'react-icons/fi';
import './JobDetails.css';
import '../pages/Jobs.css';
import '../components/AiMatchProcessingBar.css';
import LoadingButton from '../components/LoadingButton';
import IconButton from '../components/IconButton';
import AiMatchProcessingBar from '../components/AiMatchProcessingBar';
import JobFormModal from '../components/JobFormModal';
import { listingTypeLabel, listingTypeBadgeClass } from '../utils/listingType';
import { MatchScoreBadge, MatchStatusBadge } from '../utils/matchDisplay';
import { formatJobSalary } from '../utils/jobSalary';
import { appAlert, appConfirm } from '../utils/appDialog';

const TOP_SUGGESTIONS_LIMIT = 5;

const JobDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { isJobMatching, startJobMatch } = useAtsMatchRun();
  const jobMatchRunning = isJobMatching(id);
  const [selectedResume, setSelectedResume] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);

  const isStaff = user?.role === 'consultant' || user?.role === 'admin';

  const { data: job, isLoading } = useQuery(
    ['job', id],
    () => api.get(`/jobs/${id}`).then((res) => res.data)
  );

  usePageTitle(job?.title || 'Job details');

  const { data: jobMatches = [], isLoading: loadingSuggestions, isFetching: fetchingSuggestions, refetch: refetchSuggestions } = useQuery(
    ['job-matches', id],
    () => api.get(`/matches/job/${id}`).then((res) => res.data),
    {
      enabled: isStaff && Boolean(id),
      keepPreviousData: true,
      refetchOnMount: jobMatchRunning ? false : true,
    }
  );

  const topSuggestions = jobMatches.slice(0, TOP_SUGGESTIONS_LIMIT);

  const { data: resumes } = useQuery(
    'my-resumes',
    () => api.get('/resumes/my-resumes').then((res) => res.data),
    { enabled: user?.role === 'candidate' }
  );

  const matchMutation = useMutation(
    ({ resume_id, job_id }) => api.post('/matches/match', { resume_id, job_id }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('all-matches');
        queryClient.invalidateQueries('jobs-with-matches');
        queryClient.invalidateQueries('matches');
        void appAlert('Resume matched successfully!');
      },
      onError: (error) => {
        void appAlert(error.response?.data?.error || 'Failed to match resume');
      },
    }
  );

  const deleteJobMutation = useMutation(
    () => api.delete(`/jobs/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('jobs');
        navigate('/jobs');
      },
      onError: (error) => {
        void appAlert(error.response?.data?.error || 'Failed to delete job');
      },
    }
  );

  const handleAiMatch = async () => {
    if (!job) return;
    if (await appConfirm(`Run AI matching for "${job.title}"?`)) {
      try {
        await startJobMatch(id, { label: job.title });
      } catch {
        /* provider already alerts */
      }
    }
  };

  const handleMatch = () => {
    if (!selectedResume) {
      void appAlert('Please select a resume');
      return;
    }
    matchMutation.mutate({ resume_id: selectedResume, job_id: id });
  };

  const handleDelete = async () => {
    if (!job) return;
    if (await appConfirm(`Are you sure you want to delete "${job.title}"? This will soft delete the job.`)) {
      deleteJobMutation.mutate();
    }
  };

  if (isLoading) {
    return <div className="loading">Loading job details...</div>;
  }

  if (!job) {
    return <div className="error">Job not found</div>;
  }

  return (
    <div className="job-details list-page">
      <div className="job-details-toolbar page-toolbar">
        <IconButton icon={FiArrowLeft} label="Back to jobs" onClick={() => navigate('/jobs')} />
      </div>

      <div className="job-details-card">
        <div className="job-details-header">
          <h1>{job.title}</h1>
          <div className="list-page-header-actions job-details-actions">
            {user?.role === 'candidate' && job.match_score != null && (
              <MatchScoreBadge score={job.match_score} suffix="% Match" />
            )}
            <span className={`badge badge-${job.status === 'active' ? 'success' : job.status === 'closed' ? 'warning' : job.status === 'draft' ? 'info' : 'danger'}`}>
              {job.status}
            </span>
            {isStaff && (
              <span className={`badge badge-${listingTypeBadgeClass(job.listing_type)}`}>
                {listingTypeLabel(job.listing_type)}
              </span>
            )}
            {isStaff && (
              <>
                <IconButton
                  icon={FiZap}
                  label="AI match candidates"
                  size="sm"
                  onClick={handleAiMatch}
                  disabled={jobMatchRunning}
                  loading={jobMatchRunning}
                />
                <IconButton
                  icon={FiEdit}
                  label="Edit job"
                  size="sm"
                  onClick={() => setShowEditModal(true)}
                />
                <IconButton
                  icon={FiTrash2}
                  label="Delete job"
                  variant="danger"
                  size="sm"
                  onClick={handleDelete}
                  disabled={deleteJobMutation.isLoading}
                  loading={deleteJobMutation.isLoading}
                />
              </>
            )}
          </div>
        </div>

        <p className="job-company">{job.company}</p>
        {isStaff && job.listing_type === 'web' && (
          <p className="form-hint" style={{ marginTop: 8 }}>
            This job is published on the public career site API (<code>GET /api/careers/jobs</code>).
          </p>
        )}

        <div className="job-meta">
          {job.location && (
            <div className="meta-item">
              <FiMapPin /> {job.location}
            </div>
          )}
          {formatJobSalary(job) && (
            <div className="meta-item job-salary-inline">{formatJobSalary(job)}</div>
          )}
          {job.employment_type && (
            <div className="meta-item">{job.employment_type}</div>
          )}
          {job.experience_level && (
            <div className="meta-item">Experience: {job.experience_level}</div>
          )}
        </div>

        <div className="job-section">
          <h2>Description</h2>
          <p>{job.description || 'No description provided.'}</p>
        </div>

        {job.required_skills && job.required_skills.length > 0 && (
          <div className="job-section">
            <h2>Required Skills</h2>
            <div className="skills-list">
              {job.required_skills.map((skill, idx) => (
                <span key={idx} className="skill-tag">{skill}</span>
              ))}
            </div>
          </div>
        )}

        {job.preferred_skills && job.preferred_skills.length > 0 && (
          <div className="job-section">
            <h2>Preferred Skills</h2>
            <div className="skills-list">
              {job.preferred_skills.map((skill, idx) => (
                <span key={idx} className="skill-tag skill-tag-secondary">{skill}</span>
              ))}
            </div>
          </div>
        )}

        {job.external_apply_link && (
          <div className="job-section">
            <h2>Apply for this Position</h2>
            <a
              href={job.external_apply_link}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary external-apply-btn"
            >
              <FiExternalLink /> Apply externally
            </a>
            <p className="external-link-hint">This will open the company's application page in a new tab.</p>
          </div>
        )}

        {user?.role === 'candidate' && resumes && resumes.length > 0 && (
          <div className="job-section">
            <h2>Prepare your application</h2>
            <p className="external-link-hint">
              Score this role against your resume in Resume Builder before you apply — see keyword gaps and tailoring tips.
            </p>
            <Link
              to={`/resumes/${resumes[0].id}/studio?job_id=${id}`}
              className="btn btn-primary"
              style={{ marginRight: '0.5rem' }}
            >
              <FiRefreshCw /> Score in Resume Builder
            </Link>
          </div>
        )}

        {user?.role === 'candidate' && resumes && resumes.length > 0 && (
          <div className="job-section">
            <h2>Match your resume</h2>
            <div className="match-resume-panel">
              <div className="match-resume">
                <div className="match-resume-select-wrap">
                  <label htmlFor="job-detail-resume">Resume</label>
                  <select
                    id="job-detail-resume"
                    value={selectedResume}
                    onChange={(e) => setSelectedResume(e.target.value)}
                  >
                    <option value="">Select a resume</option>
                    {resumes.map((resume) => (
                      <option key={resume.id} value={resume.id}>
                        {resume.file_name || `Resume ${resume.id}`}
                      </option>
                    ))}
                  </select>
                </div>
                <LoadingButton
                  className="btn btn-primary"
                  icon={FiRefreshCw}
                  loading={matchMutation.isLoading}
                  loadingLabel="Matching…"
                  onClick={handleMatch}
                >
                  Match resume
                </LoadingButton>
              </div>
            </div>
          </div>
        )}
      </div>

      {isStaff && (
        <div
          className={`job-details-card job-suggested-candidates ${jobMatchRunning ? 'is-matching' : ''}`}
          aria-busy={jobMatchRunning || undefined}
        >
          <div className="job-suggested-header">
            <div>
              <h2>
                <FiAward aria-hidden /> Top candidate suggestions
              </h2>
              <p className="job-suggested-subtitle">
                Highest-ranked matches for this role based on stored match scores.
              </p>
            </div>
            <div className="job-suggested-header-actions">
              <IconButton
                icon={FiRefreshCw}
                label="Refresh suggestions"
                size="sm"
                loading={fetchingSuggestions || jobMatchRunning}
                disabled={jobMatchRunning}
                onClick={() => refetchSuggestions()}
              />
              {jobMatches.length > TOP_SUGGESTIONS_LIMIT && (
                <Link to="/matches" className="btn btn-secondary btn-sm">
                  View all {jobMatches.length} matches
                </Link>
              )}
            </div>
          </div>

          <AiMatchProcessingBar
            matching={jobMatchRunning}
            refreshing={fetchingSuggestions && !jobMatchRunning && !loadingSuggestions}
            label={
              jobMatchRunning
                ? 'AI matching in progress — scoring candidates against this job…'
                : undefined
            }
          />

          {loadingSuggestions ? (
            <p className="job-suggested-empty">Loading suggestions…</p>
          ) : topSuggestions.length > 0 ? (
            <ol className="job-suggested-list">
              {topSuggestions.map((match, index) => (
                <li key={match.id} className="job-suggested-item">
                  <span className={`job-suggested-rank${index < 3 ? ` job-suggested-rank--${index + 1}` : ''}`}>
                    #{index + 1}
                  </span>
                  <div className="job-suggested-body">
                    <div className="job-suggested-top">
                      <div className="job-suggested-identity">
                        <FiUser aria-hidden className="job-suggested-icon" />
                        <div>
                          <strong>
                            {match.first_name} {match.last_name}
                          </strong>
                          <span className="job-suggested-email">{match.email}</span>
                        </div>
                      </div>
                      <MatchScoreBadge score={match.match_score} suffix="% match" />
                    </div>
                    <div className="job-suggested-meta">
                      {match.current_job_title && (
                        <span>{match.current_job_title}</span>
                      )}
                      {match.job_classification_name && (
                        <span className="job-suggested-classification">
                          {match.current_job_title ? ' · ' : ''}
                          {match.job_classification_name}
                        </span>
                      )}
                      {match.status && match.status !== 'pending' && (
                        <MatchStatusBadge status={match.status} />
                      )}
                    </div>
                    {match.notes && (
                      <p className="job-suggested-notes">{match.notes}</p>
                    )}
                  </div>
                  <Link
                    to={`/candidates/${match.candidate_id}`}
                    className="btn btn-secondary btn-sm job-suggested-profile-btn"
                  >
                    View profile
                  </Link>
                </li>
              ))}
            </ol>
          ) : (
            <div className={`job-suggested-empty-state ${jobMatchRunning ? 'is-matching' : ''}`}>
              <p>No ranked candidates yet for this job.</p>
              <LoadingButton
                className="btn btn-primary btn-sm"
                icon={FiZap}
                loading={jobMatchRunning}
                loadingLabel="AI matching…"
                onClick={handleAiMatch}
              >
                Run AI match
              </LoadingButton>
            </div>
          )}
        </div>
      )}

      {isStaff && (
        <JobFormModal
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          job={job}
        />
      )}
    </div>
  );
};

export default JobDetails;
